import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../hooks/useTheme';
import Card, { CardHeader, CardTitle, CardDescription, CardContent } from './ui/Card';
import Button from './ui/Button';
import VoiceAlertModal from './VoiceAlertModal';
import { theme } from '../styles/design-system';
import {
  Mic,
  Bell,
  RotateCcw,
  Trash2,
  Play,
  Volume2,
  Activity,
  CheckCircle2,
  XCircle,
  Clock,
  TrendingUp,
  AlertCircle,
  Zap
} from 'lucide-react';

const VoiceAlertsPage = () => {
  const { user, isAuthenticated } = useAuth();
  const { isDark } = useTheme();
  const [alerts, setAlerts] = useState([]);
  const [alertHistory, setAlertHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [systemStatus, setSystemStatus] = useState(null);
  const [wsConnection, setWsConnection] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState('disconnected');
  const [activeTab, setActiveTab] = useState('active');

  useEffect(() => {
    if (isAuthenticated && user?.id) {
      loadAlerts();
      loadAlertHistory();
      loadSystemStatus();
      connectWebSocket();
    }

    return () => {
      if (wsConnection) {
        wsConnection.close();
      }
    };
  }, [isAuthenticated, user?.id]);

  const loadAlerts = async () => {
    if (!user?.id) return;

    setIsLoading(true);
    try {
      const response = await fetch(`/api/voice-alerts/list?user_id=${user.id}`);
      const data = await response.json();
      if (data.success) {
        setAlerts(data.alerts);
      }
    } catch (error) {
      console.error('Error loading alerts:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadAlertHistory = async () => {
    if (!user?.id) return;

    try {
      const response = await fetch(`/api/voice-alerts/history?user_id=${user.id}&limit=20`);
      const data = await response.json();
      if (data.success) {
        setAlertHistory(data.history);
      }
    } catch (error) {
      console.error('Error loading alert history:', error);
    }
  };

  const loadSystemStatus = async () => {
    try {
      const response = await fetch('/api/voice-alerts/status');
      const data = await response.json();
      if (data.success) {
        setSystemStatus(data.system_status);
      }
    } catch (error) {
      console.error('Error loading system status:', error);
    }
  };

  const connectWebSocket = () => {
    if (!user?.id) return;

    try {
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const wsUrl = `${protocol}//${window.location.host}/api/voice-alerts/ws/${user.id}`;

      const ws = new WebSocket(wsUrl);

      ws.onopen = () => {
        console.log('Voice alerts WebSocket connected');
        setConnectionStatus('connected');
        setWsConnection(ws);
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          handleWebSocketMessage(data);
        } catch (error) {
          console.error('Error parsing WebSocket message:', error);
        }
      };

      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
        setConnectionStatus('error');
      };

      ws.onclose = () => {
        console.log('WebSocket closed');
        setConnectionStatus('disconnected');
        // Attempt to reconnect after 5 seconds
        setTimeout(() => {
          if (isAuthenticated && user?.id) {
            connectWebSocket();
          }
        }, 5000);
      };
    } catch (error) {
      console.error('Error connecting WebSocket:', error);
      setConnectionStatus('error');
    }
  };

  const handleWebSocketMessage = (data) => {
    if (data.type === 'alert_triggered') {
      // Reload alerts and history when an alert is triggered
      loadAlerts();
      loadAlertHistory();

      // Speak the alert if voice is supported
      if (data.alert && data.alert.message) {
        speakText(data.alert.message, data.alert.voice_config);
      }

      // Show browser notification if permitted
      showNotification(data.alert);
    } else if (data.type === 'status_update') {
      loadSystemStatus();
    }
  };

  const speakText = (text, voiceConfig = {}) => {
    if ('speechSynthesis' in window) {
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.rate = voiceConfig.rate || 1.0;
      utterance.pitch = voiceConfig.pitch || 1.0;
      utterance.volume = voiceConfig.volume || 1.0;

      if (voiceConfig.voice_type) {
        const voices = window.speechSynthesis.getVoices();
        const selectedVoice = voices.find(v => v.name.toLowerCase().includes(voiceConfig.voice_type.toLowerCase()));
        if (selectedVoice) {
          utterance.voice = selectedVoice;
        }
      }

      window.speechSynthesis.speak(utterance);
    }
  };

  const showNotification = (alert) => {
    if ('Notification' in window && Notification.permission === 'granted') {
      new Notification('Voice Alert Triggered', {
        body: alert.message || `Alert for ${alert.symbol}`,
        icon: '/favicon.ico',
        badge: '/favicon.ico'
      });
    }
  };

  const requestNotificationPermission = async () => {
    if ('Notification' in window) {
      const permission = await Notification.requestPermission();
      if (permission === 'granted') {
        alert('Notifications enabled successfully!');
      } else {
        alert('Notification permission denied.');
      }
    } else {
      alert('Notifications are not supported in this browser.');
    }
  };

  const deleteAlert = async (alertId) => {
    if (!window.confirm('Are you sure you want to delete this alert?')) {
      return;
    }

    try {
      const response = await fetch(`/api/voice-alerts/${alertId}`, {
        method: 'DELETE'
      });
      const data = await response.json();
      if (data.success) {
        loadAlerts();
        alert('Voice alert deleted successfully');
      } else {
        alert(`Error: ${data.message}`);
      }
    } catch (error) {
      console.error('Error deleting alert:', error);
      alert('Error deleting alert. Please try again.');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return '#10b981';
      case 'triggered': return '#f59e0b';
      case 'paused': return '#6b7280';
      default: return '#9ca3af';
    }
  };

  if (!isAuthenticated) {
    return (
      <div style={styles.authPrompt}>
        <h2 style={styles.authTitle}>🔐 Authentication Required</h2>
        <p style={styles.authMessage}>
          Please login to access the Voice Alerts system.
        </p>
      </div>
    );
  }

  const activeAlerts = alerts.filter(a => a.status === 'active').length;
  const triggeredAlerts = alerts.filter(a => a.status === 'triggered').length;

  return (
    <div style={styles.container}>
      {/* Hero Section */}
      <div style={styles.heroSection}>
        <div style={styles.heroContent}>
          <div style={styles.heroTitle}>
            <Mic size={32} />
            <span>Voice Alerts</span>
          </div>
          <p style={styles.heroSubtitle}>
            Advanced voice notifications for your trading alerts with real-time monitoring
          </p>
        </div>
        <div style={styles.heroActions}>
          <Button
            onClick={() => setShowCreateModal(true)}
            variant="secondary"
            icon={<Mic size={16} />}
          >
            Create Voice Alert
          </Button>
          <Button
            onClick={requestNotificationPermission}
            variant="outline"
            style={{ borderColor: 'rgba(255,255,255,0.3)', color: 'white' }}
            icon={<Bell size={16} />}
          >
            Enable Notifications
          </Button>
          <Button
            onClick={loadAlerts}
            disabled={isLoading}
            variant="outline"
            style={{ borderColor: 'rgba(255,255,255,0.3)', color: 'white' }}
            icon={<RotateCcw size={16} />}
          >
            Refresh
          </Button>
        </div>
      </div>

      {/* Stats Dashboard */}
      <div style={styles.dashboardGrid}>
        <Card style={styles.metricCard}>
          <CardContent>
            <div style={styles.metricHeader}>
              <span style={styles.metricTitle}>Connection</span>
              <Activity size={16} color="var(--text-tertiary)" />
            </div>
            <div style={styles.metricValue}>
              {connectionStatus === 'connected' && <span style={{ color: '#10b981' }}>Connected</span>}
              {connectionStatus === 'disconnected' && <span style={{ color: '#ef4444' }}>Disconnected</span>}
              {connectionStatus === 'error' && <span style={{ color: '#ef4444' }}>Error</span>}
            </div>
            <div style={styles.metricSubtext}>
              WebSocket status
            </div>
          </CardContent>
        </Card>

        <Card style={styles.metricCard}>
          <CardContent>
            <div style={styles.metricHeader}>
              <span style={styles.metricTitle}>Total Alerts</span>
              <Bell size={16} color="var(--text-tertiary)" />
            </div>
            <div style={styles.metricValue}>{alerts.length}</div>
            <div style={styles.metricSubtext}>
              All configured alerts
            </div>
          </CardContent>
        </Card>

        <Card style={styles.metricCard}>
          <CardContent>
            <div style={styles.metricHeader}>
              <span style={styles.metricTitle}>Active</span>
              <CheckCircle2 size={16} color="var(--text-tertiary)" />
            </div>
            <div style={styles.metricValue}>
              <span style={{ color: '#10b981' }}>{activeAlerts}</span>
            </div>
            <div style={styles.metricSubtext}>
              Currently monitoring
            </div>
          </CardContent>
        </Card>

        <Card style={styles.metricCard}>
          <CardContent>
            <div style={styles.metricHeader}>
              <span style={styles.metricTitle}>Monitoring</span>
              <Zap size={16} color="var(--text-tertiary)" />
            </div>
            <div style={styles.metricValue}>
              {systemStatus?.monitoring_active ?
                <span style={{ color: '#10b981' }}>Active</span> :
                <span style={{ color: '#ef4444' }}>Inactive</span>
              }
            </div>
            <div style={styles.metricSubtext}>
              System status
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Custom Tabs */}
      <div style={styles.tabsContainer}>
        {['active', 'history'].map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            style={{
              ...styles.tabButton,
              ...(activeTab === tab ? styles.activeTab : {})
            }}
          >
            {tab === 'active' ? 'Active Alerts' : 'Alert History'}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'active' && (
        <Card>
          <CardHeader>
            <CardTitle style={styles.sectionTitle}>
              <Bell size={20} /> Active Voice Alerts ({alerts.length})
            </CardTitle>
            <CardDescription>Your currently active voice alerts</CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div style={styles.emptyState}>Loading alerts...</div>
            ) : alerts.length === 0 ? (
              <div style={styles.emptyState}>
                <Mic size={48} color="var(--text-tertiary)" style={{ marginBottom: '16px' }} />
                <h3 style={{ color: 'var(--text-primary)', marginBottom: '8px' }}>No Voice Alerts</h3>
                <p style={{ color: 'var(--text-secondary)' }}>
                  Create your first voice alert to get started with real-time notifications.
                </p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing[4] }}>
                {alerts.map((alert) => (
                  <div key={alert.id} style={styles.alertCard}>
                    <div style={styles.alertCardHeader}>
                      <div style={styles.alertTitleRow}>
                        <TrendingUp size={20} color="#6366f1" />
                        <h3 style={styles.alertSymbol}>{alert.symbol}</h3>
                        <span style={{
                          ...styles.statusBadge,
                          backgroundColor: getStatusColor(alert.status) + '20',
                          color: getStatusColor(alert.status),
                          border: `1px solid ${getStatusColor(alert.status)}40`
                        }}>
                          {alert.status}
                        </span>
                      </div>
                    </div>

                    <div style={styles.alertCardGrid}>
                      <div style={styles.alertDetail}>
                        <span style={styles.alertLabel}>Type</span>
                        <span style={styles.alertValue}>{alert.alert_type?.replace('_', ' ')}</span>
                      </div>
                      <div style={styles.alertDetail}>
                        <span style={styles.alertLabel}>Target</span>
                        <span style={styles.alertValue}>₹{alert.target_value}</span>
                      </div>
                      <div style={styles.alertDetail}>
                        <span style={styles.alertLabel}>Voice</span>
                        <span style={styles.alertValue}>{alert.voice_config?.voice_type || 'default'}</span>
                      </div>
                      {alert.current_value && (
                        <div style={styles.alertDetail}>
                          <span style={styles.alertLabel}>Current</span>
                          <span style={styles.alertValue}>₹{alert.current_value}</span>
                        </div>
                      )}
                    </div>

                    {alert.user_message && (
                      <div style={styles.alertMessageBox}>
                        <AlertCircle size={14} />
                        <span>{alert.user_message}</span>
                      </div>
                    )}

                    <div style={styles.alertCardActions}>
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() => deleteAlert(alert.id)}
                        icon={<Trash2 size={14} />}
                      >
                        Delete
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {activeTab === 'history' && (
        <Card>
          <CardHeader>
            <CardTitle style={styles.sectionTitle}>
              <Clock size={20} /> Recent Alert History ({alertHistory.length})
            </CardTitle>
            <CardDescription>Recently triggered voice alerts</CardDescription>
          </CardHeader>
          <CardContent>
            {alertHistory.length === 0 ? (
              <div style={styles.emptyState}>
                <Clock size={48} color="var(--text-tertiary)" style={{ marginBottom: '16px' }} />
                <h3 style={{ color: 'var(--text-primary)', marginBottom: '8px' }}>No History</h3>
                <p style={{ color: 'var(--text-secondary)' }}>
                  Triggered alerts will appear here
                </p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing[3] }}>
                {alertHistory.slice(0, 10).map((history) => (
                  <div key={history.id} style={styles.historyCard}>
                    <div style={styles.historyHeader}>
                      <div style={styles.historyTitleRow}>
                        <Volume2 size={18} color="#10b981" />
                        <h4 style={styles.historySymbol}>{history.symbol}</h4>
                      </div>
                      <span style={styles.historyTime}>
                        <Clock size={14} />
                        {new Date(history.triggered_at).toLocaleString()}
                      </span>
                    </div>
                    <p style={styles.historyMessage}>{history.message}</p>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => speakText(history.message, history.voice_config)}
                      icon={<Play size={14} />}
                    >
                      Replay
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Voice Alert Modal */}
      <VoiceAlertModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onAlertCreated={() => {
          loadAlerts();
          loadAlertHistory();
        }}
      />
    </div>
  );
};

const styles = {
  container: {
    maxWidth: '1400px',
    margin: '0 auto',
    padding: theme.spacing[4],
    fontFamily: theme.typography.fontFamily.sans.join(', '),
    backgroundColor: 'var(--bg-secondary)',
    minHeight: '100vh',
  },
  heroSection: {
    background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)', // Pink/Yellow gradient for Voice
    borderRadius: theme.borderRadius['2xl'],
    padding: theme.spacing[8],
    marginBottom: theme.spacing[8],
    color: 'white',
    position: 'relative',
    overflow: 'hidden',
    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing[4],
  },
  heroContent: {
    position: 'relative',
    zIndex: 1,
  },
  heroTitle: {
    fontSize: theme.typography.fontSize['4xl'],
    fontWeight: theme.typography.fontWeight.bold,
    margin: 0,
    letterSpacing: '-0.025em',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing[3],
  },
  heroSubtitle: {
    fontSize: theme.typography.fontSize.lg,
    color: 'white',
    opacity: 0.95,
    margin: '8px 0 0 0',
    fontWeight: theme.typography.fontWeight.medium,
    maxWidth: '600px',
  },
  heroActions: {
    display: 'flex',
    gap: theme.spacing[3],
    zIndex: 1,
    flexWrap: 'wrap',
  },
  dashboardGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: theme.spacing[6],
    marginBottom: theme.spacing[8],
  },
  metricCard: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  metricHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing[2],
  },
  metricTitle: {
    fontSize: theme.typography.fontSize.sm,
    color: 'var(--text-secondary)',
    fontWeight: theme.typography.fontWeight.medium,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },
  metricValue: {
    fontSize: theme.typography.fontSize['3xl'],
    fontWeight: theme.typography.fontWeight.bold,
    color: 'var(--text-primary)',
  },
  metricSubtext: {
    fontSize: theme.typography.fontSize.xs,
    color: 'var(--text-tertiary)',
    marginTop: theme.spacing[1],
  },
  tabsContainer: {
    display: 'flex',
    gap: theme.spacing[1],
    backgroundColor: 'var(--bg-tertiary)',
    padding: theme.spacing[1],
    borderRadius: theme.borderRadius.lg,
    marginBottom: theme.spacing[6],
    overflowX: 'auto',
  },
  tabButton: {
    padding: `${theme.spacing[2]} ${theme.spacing[4]}`,
    borderRadius: theme.borderRadius.md,
    border: 'none',
    background: 'transparent',
    color: 'var(--text-secondary)',
    fontWeight: theme.typography.fontWeight.medium,
    cursor: 'pointer',
    fontSize: theme.typography.fontSize.sm,
    whiteSpace: 'nowrap',
    transition: 'all 0.2s ease',
  },
  activeTab: {
    backgroundColor: 'var(--bg-primary)',
    color: 'var(--text-primary)',
    boxShadow: theme.shadows.sm,
  },
  sectionTitle: {
    fontSize: theme.typography.fontSize.xl,
    fontWeight: theme.typography.fontWeight.bold,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing[2],
  },
  emptyState: {
    textAlign: 'center',
    padding: theme.spacing[8],
    color: 'var(--text-secondary)',
  },
  alertCard: {
    backgroundColor: 'var(--bg-primary)',
    border: '1px solid var(--border-primary)',
    borderRadius: theme.borderRadius.lg,
    padding: theme.spacing[4],
  },
  alertCardHeader: {
    marginBottom: theme.spacing[3],
  },
  alertTitleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing[2],
  },
  alertSymbol: {
    fontSize: theme.typography.fontSize.xl,
    fontWeight: theme.typography.fontWeight.bold,
    color: 'var(--text-primary)',
    margin: 0,
  },
  statusBadge: {
    padding: '4px 12px',
    borderRadius: '999px',
    fontSize: theme.typography.fontSize.xs,
    fontWeight: theme.typography.fontWeight.semibold,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },
  alertCardGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
    gap: theme.spacing[3],
    marginBottom: theme.spacing[3],
  },
  alertDetail: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing[1],
  },
  alertLabel: {
    fontSize: theme.typography.fontSize.xs,
    color: 'var(--text-tertiary)',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },
  alertValue: {
    fontSize: theme.typography.fontSize.base,
    fontWeight: theme.typography.fontWeight.semibold,
    color: 'var(--text-primary)',
  },
  alertMessageBox: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing[2],
    padding: theme.spacing[3],
    backgroundColor: 'var(--bg-tertiary)',
    borderRadius: theme.borderRadius.md,
    fontSize: theme.typography.fontSize.sm,
    color: 'var(--text-secondary)',
    marginBottom: theme.spacing[3],
    border: '1px solid var(--border-primary)',
  },
  alertCardActions: {
    display: 'flex',
    gap: theme.spacing[2],
    justifyContent: 'flex-end',
  },
  historyCard: {
    backgroundColor: 'var(--bg-primary)',
    border: '1px solid var(--border-primary)',
    borderRadius: theme.borderRadius.lg,
    padding: theme.spacing[4],
  },
  historyHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing[2],
    flexWrap: 'wrap',
    gap: theme.spacing[2],
  },
  historyTitleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing[2],
  },
  historySymbol: {
    fontSize: theme.typography.fontSize.lg,
    fontWeight: theme.typography.fontWeight.bold,
    color: 'var(--text-primary)',
    margin: 0,
  },
  historyTime: {
    fontSize: theme.typography.fontSize.sm,
    color: 'var(--text-tertiary)',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing[1],
  },
  historyMessage: {
    fontSize: theme.typography.fontSize.base,
    color: 'var(--text-secondary)',
    marginBottom: theme.spacing[3],
  },
  authPrompt: {
    textAlign: 'center',
    padding: theme.spacing[16],
    backgroundColor: 'var(--card-bg)',
    borderRadius: theme.borderRadius['2xl'],
    boxShadow: theme.shadows.lg,
    maxWidth: '600px',
    margin: '0 auto',
  },
  authTitle: {
    fontSize: theme.typography.fontSize['3xl'],
    fontWeight: theme.typography.fontWeight.bold,
    color: 'var(--text-primary)',
    marginBottom: theme.spacing[4],
  },
  authMessage: {
    fontSize: theme.typography.fontSize.lg,
    color: 'var(--text-secondary)',
    lineHeight: theme.typography.lineHeight.relaxed,
  },
};

export default VoiceAlertsPage;
